import {
  Box, Card, CardContent, Chip, CircularProgress, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import InboxIcon from '@mui/icons-material/Inbox'
import SearchIcon from '@mui/icons-material/Search'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { getUrl, httpClient } from '../providers/data'
import { Verzoek } from '../types/apiRequests'
import {
  DoelVerzoekKey, VerzoekTypeKey,
  doelVerzoekColor, formatDate, verzoekTypeColor,
} from './verzoekenoverzichtConstants'

const STORAGE_KEY_START = 'verzoekenoverzicht_startDatum'
const STORAGE_KEY_EIND = 'verzoekenoverzicht_eindDatum'

const getDefaultDates = (): { startDatum: string; eindDatum: string } => {
  const end = new Date()
  const start = new Date()
  start.setMonth(start.getMonth() - 6)
  return {
    startDatum: start.toISOString().split('T')[0],
    eindDatum: end.toISOString().split('T')[0],
  }
}

const getStoredDates = (): { startDatum: string; eindDatum: string } => {
  const defaults = getDefaultDates()
  return {
    startDatum: localStorage.getItem(STORAGE_KEY_START) || defaults.startDatum,
    eindDatum: localStorage.getItem(STORAGE_KEY_EIND) || defaults.eindDatum,
  }
}

const Verzoekenoverzicht: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()

  const stored = getStoredDates()
  const [startDatum, setStartDatum] = useState(stored.startDatum)
  const [eindDatum, setEindDatum] = useState(stored.eindDatum)
  const [search, setSearch] = useState('')

  const [verzoeken, setVerzoeken] = useState<Verzoek[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [loading, setLoading] = useState(false)

  const handleStartDatum = (value: string) => {
    setStartDatum(value)
    setPage(0)
    localStorage.setItem(STORAGE_KEY_START, value)
  }

  const handleEindDatum = (value: string) => {
    setEindDatum(value)
    setPage(0)
    localStorage.setItem(STORAGE_KEY_EIND, value)
  }

  const fetchVerzoeken = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        size: String(rowsPerPage),
      })
      if (startDatum) params.set('startDatum', startDatum)
      if (eindDatum) params.set('eindDatum', eindDatum)

      const url = `${getUrl()}/verzoekenoverzicht?${params.toString()}`
      const response = await httpClient(url)

      const contentRange = response.headers.get('Content-Range')
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)/)
        if (match) setTotal(parseInt(match[1], 10))
      }

      setVerzoeken(response.json as Verzoek[])
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
      setVerzoeken([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [startDatum, eindDatum, page, rowsPerPage, notify])

  useEffect(() => {
    void fetchVerzoeken()
  }, [fetchVerzoeken])

  const filtered = useMemo(() => {
    if (!search.trim()) return verzoeken
    const q = search.toLowerCase()
    return verzoeken.filter((v) =>
      v.verzoeknummer.toLowerCase().includes(q) ||
      (v.verzoekNaam ?? '').toLowerCase().includes(q) ||
      v.verzoekType.toLowerCase().includes(q) ||
      v.doelVerzoek.toLowerCase().includes(q) ||
      (v.bevoegdGezag?.naam ?? '').toLowerCase().includes(q) ||
      (v.behandeldienst?.naam ?? '').toLowerCase().includes(q),
    )
  }, [verzoeken, search])

  return (
    <Box sx={{ mt: 2, ml: 2, mr: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('verzoekenoverzicht.title')}
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: '16px !important' }}>
          <FilterListIcon color="action" />
          <TextField
            type="date"
            size="small"
            label={translate('verzoekenoverzicht.startDatum')}
            value={startDatum}
            onChange={(e) => handleStartDatum(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            label={translate('verzoekenoverzicht.eindDatum')}
            value={eindDatum}
            onChange={(e) => handleEindDatum(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label={translate('verzoekenoverzicht.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ ml: 'auto', minWidth: 240 }}
          />
        </CardContent>
      </Card>

      <Card>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && filtered.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, color: 'text.secondary' }}>
            <InboxIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
            <Typography variant="body1">
              {translate('verzoekenoverzicht.noResults')}
            </Typography>
          </Box>
        )}
        {!loading && filtered.length > 0 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('verzoekenoverzicht.columns.verzoeknummer')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.verzoekNaam')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.verzoekType')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.doelVerzoek')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.datumIngediend')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.bevoegdGezag')}</TableCell>
                    <TableCell>{translate('verzoekenoverzicht.columns.behandeldienst')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((v) => (
                    <TableRow
                      key={`${v.verzoeknummer}-${v.volgnummer}`}
                      hover
                      onClick={() => void navigate(`/verzoekenoverzicht/${v.verzoeknummer}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {v.verzoeknummer}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {translate('verzoekenoverzicht.columns.volgnummer')}: {v.volgnummer}
                        </Typography>
                      </TableCell>
                      <TableCell>{v.verzoekNaam ?? '–'}</TableCell>
                      <TableCell>
                        <Chip
                          label={v.verzoekType}
                          color={verzoekTypeColor[v.verzoekType as VerzoekTypeKey] ?? 'default'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={v.doelVerzoek}
                          color={doelVerzoekColor[v.doelVerzoek as DoelVerzoekKey] ?? 'default'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(v.datumIngediend)}</TableCell>
                      <TableCell>{v.bevoegdGezag?.naam ?? '–'}</TableCell>
                      <TableCell>{v.behandeldienst?.naam ?? '–'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100]}
              labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
            />
          </>
        )}
      </Card>
    </Box>
  )
}

export default Verzoekenoverzicht
